import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { rupiah, tanggalID } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { subState } from "@/components/app/SubscriptionNotice";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, Lock, Crown, Upload, Landmark } from "lucide-react";

const PERIODE = { bulanan: "Bulanan", tahunan: "Tahunan" };
const PAY_STATUS = { pending: ["Menunggu Konfirmasi", "bg-blue-100 text-blue-700"], approved: ["Dikonfirmasi", "bg-emerald-100 text-emerald-700"], rejected: ["Ditolak", "bg-rose-100 text-rose-700"] };

function CurrentPlan({ sub }) {
  const st = subState(sub);
  return (
    <div className="card-flat p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4" data-testid="sub-current">
      <span className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--brand-soft)", color: "var(--brand)" }}><Crown size={22}/></span>
      <div className="flex-1">
        <div className="text-xs uppercase tracking-wider text-slate-400">Paket saat ini</div>
        <div className="text-xl font-semibold text-slate-900" data-testid="sub-current-name">{sub?.plan_name}</div>
        <div className="text-sm text-slate-500 mt-0.5" data-testid="sub-current-period">
          {sub?.status === "trial" ? "Masa coba" : sub?.period === "lifetime" ? "Tanpa batas waktu" : `Langganan ${PERIODE[sub?.period] || ""}`}
          {sub?.expires_at && <> · berakhir {tanggalID(sub.expires_at)}{sub.days_left > 0 && ` (${sub.days_left} hari lagi)`}</>}
        </div>
      </div>
      {st && <div className={`text-xs px-3 py-2 rounded-md ${st.tone === "rose" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`} data-testid="sub-current-warning">{st.title}</div>}
    </div>
  );
}

function PlanCard({ p, features, periode, current, canPay, onPick }) {
  const price = periode === "bulanan" ? p.price_monthly : p.price_yearly;
  return (
    <div className={`reg-card rounded-2xl border-2 bg-white p-5 flex flex-col gap-4 ${current ? "border-[color:var(--brand)]" : "border-slate-200"}`} data-testid={`plan-card-${p.id}`}>
      <div>
        <div className="flex items-center justify-between gap-2">
          <div className="text-lg font-semibold text-slate-900">{p.name}</div>
          {current && <span className="text-[10px] px-2 py-0.5 rounded-full text-white" style={{ background: "var(--brand)" }}>Paket Anda</span>}
        </div>
        {p.description && <div className="text-xs text-slate-500 mt-1">{p.description}</div>}
      </div>
      <div>
        <span className="text-2xl font-bold text-slate-900">{price > 0 ? rupiah(price) : "Rp -"}</span>
        <span className="text-xs text-slate-500"> / {periode === "bulanan" ? "bulan" : "tahun"}</span>
        <div className="text-[11px] text-slate-400 mt-1">{p.max_users < 0 ? "User tanpa batas" : `Maks. ${p.max_users} user`}</div>
      </div>
      <ul className="space-y-1.5 flex-1">
        {features.map((f) => {
          const on = p.features.includes(f.k);
          return (
            <li key={f.k} className={`flex items-start gap-2 text-xs ${on ? "text-slate-700" : "text-slate-300"}`}>
              {on ? <Check size={13} className="mt-0.5 shrink-0" style={{ color: "var(--brand)" }}/> : <Lock size={12} className="mt-0.5 shrink-0"/>}{f.l}
            </li>
          );
        })}
      </ul>
      {canPay && <Button onClick={() => onPick(p)} disabled={price <= 0} className="brand-btn rounded-sm w-full" data-testid={`plan-pay-${p.id}`}>{current ? "Perpanjang" : "Pilih Paket"}</Button>}
    </div>
  );
}

function PayDialog({ plan, periode, banks, note, onClose, onDone }) {
  const [proof, setProof] = useState("");
  const [fname, setFname] = useState("");
  const [catatan, setCatatan] = useState("");
  const [loading, setLoading] = useState(false);
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error("Ukuran bukti maksimal 2 MB");
    const r = new FileReader();
    r.onload = () => { setProof(r.result); setFname(f.name); };
    r.readAsDataURL(f);
  };
  const submit = async () => {
    if (!proof) return toast.error("Unggah bukti transfer dulu");
    setLoading(true);
    try { await api.post("/langganan/bayar", { plan_id: plan.id, periode, proof_b64: proof, catatan }); toast.success("Bukti terkirim. Menunggu konfirmasi Super Admin."); onDone(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  const amount = periode === "bulanan" ? plan.price_monthly : plan.price_yearly;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="pay-dialog">
        <DialogHeader><DialogTitle>Bayar {plan.name} — {PERIODE[periode]}</DialogTitle></DialogHeader>
        <div className="rounded-lg p-4 text-center" style={{ background: "var(--brand-soft)" }}>
          <div className="text-xs text-slate-500">Total transfer</div>
          <div className="text-2xl font-bold brand-text" data-testid="pay-amount">{rupiah(amount)}</div>
        </div>
        <div className="space-y-2" data-testid="pay-banks">
          {banks.length === 0 && <p className="text-sm text-rose-600">Rekening tujuan belum diatur Super Admin.</p>}
          {banks.map((b, i) => (
            <div key={i} className="flex items-center gap-3 border border-slate-200 rounded-md p-3">
              <Landmark size={18} className="text-slate-400"/>
              <div className="flex-1 text-sm"><b>{b.bank}</b> · <span className="font-mono">{b.no_rek}</span><div className="text-xs text-slate-500">a.n. {b.atas_nama}</div></div>
              <button type="button" className="text-xs brand-text" onClick={() => { navigator.clipboard?.writeText(b.no_rek); toast.success("Nomor rekening disalin"); }}>Salin</button>
            </div>
          ))}
          {note && <p className="text-xs text-slate-500 whitespace-pre-line">{note}</p>}
        </div>
        <div className="space-y-1.5">
          <Label>Bukti Transfer (JPG/PNG/PDF, maks. 2 MB) *</Label>
          <label className="flex items-center gap-2 border-2 border-dashed border-slate-200 rounded-md p-3 cursor-pointer hover:border-[color:var(--brand)] text-sm text-slate-500">
            <Upload size={16}/> {fname || "Pilih file bukti transfer"}
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={pick} data-testid="pay-proof-input"/>
          </label>
        </div>
        <div className="space-y-1.5"><Label>Catatan (opsional)</Label><Textarea rows={2} value={catatan} onChange={(e) => setCatatan(e.target.value)} placeholder="Mis. transfer dari rekening a.n. ..." data-testid="pay-note"/></div>
        <DialogFooter data-dialog-footer>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={loading} className="brand-btn" data-testid="pay-submit">{loading ? "Mengirim..." : "Kirim Bukti"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Langganan() {
  const { user, refresh } = useAuth();
  const [d, setD] = useState(null);
  const [periode, setPeriode] = useState("bulanan");
  const [pick, setPick] = useState(null);
  const load = () => api.get("/langganan/saya").then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="p-10 text-sm text-slate-400">Memuat…</div>;
  const pending = d.payments.find((p) => p.status === "pending");
  return (
    <div>
      <PageHeader title="Langganan" subtitle="Kelola paket langganan workspace Anda."/>
      <div className="p-4 md:p-8 space-y-6 max-w-6xl">
        <CurrentPlan sub={d.subscription || user?.subscription}/>
        {pending && <div className="card-flat p-4 text-sm text-blue-800 bg-blue-50 border-blue-200" data-testid="sub-pending">Pembayaran <b>{pending.plan_name}</b> ({PERIODE[pending.periode]}) {rupiah(pending.amount)} sedang menunggu konfirmasi Super Admin.</div>}
        {!d.can_pay && <p className="text-xs text-slate-500">Hanya admin workspace yang dapat membayar langganan.</p>}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-base md:text-lg font-semibold text-slate-900">Pilih Paket</h2>
          <div className="inline-flex rounded-full border border-slate-200 p-1 bg-white" data-testid="periode-toggle">
            {Object.entries(PERIODE).map(([k, l]) => (
              <button key={k} onClick={() => setPeriode(k)} data-testid={`periode-${k}`}
                className={`px-4 py-1.5 text-sm rounded-full transition-colors duration-150 ${periode === k ? "text-white" : "text-slate-600"}`} style={periode === k ? { background: "var(--brand)" } : {}}>{l}</button>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="plan-list">
          {d.plans.map((p) => <PlanCard key={p.id} p={p} features={d.features} periode={periode} current={p.id === d.subscription?.plan_id} canPay={d.can_pay && !pending} onPick={setPick}/>)}
          {d.plans.length === 0 && <p className="text-sm text-slate-500">Belum ada paket tersedia.</p>}
        </div>
        {d.payments.length > 0 && (
          <div className="card-flat overflow-x-auto" data-testid="pay-history">
            <div className="px-5 py-3 font-semibold text-slate-900 border-b border-slate-100">Riwayat Pembayaran</div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500"><th className="p-3">Tanggal</th><th className="p-3">Paket</th><th className="p-3">Nominal</th><th className="p-3">Status</th></tr></thead>
              <tbody>{d.payments.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="p-3">{tanggalID(p.created_at)}</td><td className="p-3">{p.plan_name} · {PERIODE[p.periode]}</td><td className="p-3">{rupiah(p.amount)}</td>
                  <td className="p-3"><span className={`text-[11px] px-2 py-0.5 rounded-sm ${PAY_STATUS[p.status][1]}`}>{PAY_STATUS[p.status][0]}</span>
                    {p.reject_reason && <div className="text-[11px] text-rose-600 mt-1">Alasan: {p.reject_reason}</div>}</td>
                </tr>))}</tbody>
            </table>
          </div>
        )}
      </div>
      {pick && <PayDialog plan={pick} periode={periode} banks={d.banks} note={d.payment_note} onClose={() => setPick(null)} onDone={() => { setPick(null); load(); refresh?.(); }}/>}
    </div>
  );
}
